/**
 * `wageproMigration` data hook — the "Switch to WagePro" employee + payroll
 * migration importer (wmi_plan / docs/adr/20261005_05_wagepro-migration-import.md).
 *
 * Follows the DI/never-throw shape of `use_payroll_import.ts`: every server
 * call is wrapped in a pure, exported action function that classifies failure
 * into a small UI bucket and never throws, so the wizard logic is unit-testable
 * against a fake `deps` object. The hook binds those actions to the resolved
 * OAuth entity and hides every WRITE (`stage`/`commit`/`undo`/`abort`) behind
 * `isWriteCapableGrant`, exactly like every other WagePro write surface — a
 * read-only grant gets the read calls (`template`/`getReport`/`listRuns`) only.
 *
 * Access tiers, step-up and product-tier gates live on the server
 * (`apps/api/src/trpc/routers/wagepro_migration.ts`); this hook never assumes
 * it can write — it reflects what the grant allows.
 */
import * as React from 'react';
import type { MigrationIssue, MigrationJurisdiction } from '@jumpstone/core';
import {
  getTrpcClient,
  isBadRequestTrpcError,
  isConflictTrpcError,
  isForbiddenTrpcError,
  isNotFoundTrpcError,
  isPayrollAddonRequiredError,
} from '../lib/trpc.js';
import { useAuth } from '../lib/auth/auth_context.js';
import { useOAuthEntityContext } from '../lib/auth/oauth_entity_context.js';
import { isWriteCapableGrant } from './use_pay_runs.js';

// ── Wire shapes (mirror the server router outputs) ──────────────────────────

export interface MigrationEntityInput {
  firm_id: string;
  entity_id: string;
}

/** Entity-specific inputs the browser needs to build the template. */
export interface MigrationTemplateInfo {
  country: string;
  jurisdiction: MigrationJurisdiction;
  base_currency: string;
  enabled_currencies: string[];
  leave_type_codes: string[];
  employee_number_count: number;
}

/** One staged row: every cell as a string, plus which were raw numbers. */
export interface MigrationStageRow {
  row: number;
  cells: Record<string, string>;
  numericCells?: string[];
}

export interface MigrationStageSheets {
  employees: MigrationStageRow[];
  salary_history?: MigrationStageRow[];
  leave_balances?: MigrationStageRow[];
  payroll_history?: MigrationStageRow[];
}

export interface MigrationStageInput {
  run_id?: string;
  cutover_date: string;
  sheets: MigrationStageSheets;
  links?: Record<string, string>;
}

export interface MigrationCounts {
  employees_create: number;
  employees_link: number;
  employees_skip: number;
  salary_rows: number;
  leave_rows: number;
  payroll_rows: number;
  pay_periods: number;
}

/** The staging report (also returned, minus counts/issues, by getReport). */
export interface MigrationReport {
  run_id: string;
  status: string;
  cutover_date: string | null;
  stats: Record<string, unknown> | null;
  item_counts: Record<string, number>;
  loaded: number;
  total: number;
  counts: MigrationCounts;
  issues: MigrationIssue[];
  blocking: boolean;
}

/** Read-only report returned by `getReport` (no counts/issues/blocking). */
export interface MigrationRunReport {
  run_id: string;
  status: string;
  cutover_date: string | null;
  stats: Record<string, unknown> | null;
  item_counts: Record<string, number>;
  loaded: number;
  total: number;
}

export interface MigrationRunSummary {
  id: string;
  status: string;
  conversion_date: string | null;
  scope_json: string | null;
  stats_json: string | null;
  created_at: number;
  updated_at: number;
}

export interface MigrationCommitProgress {
  status: string;
  loaded: number;
  total: number;
}

export interface MigrationUndoResult {
  voided_pay_runs: number;
  reversed_leave_adjustments: number;
  deactivated_employees: number;
}

// ── Failure classification ──────────────────────────────────────────────────

export type MigrationFailure =
  | 'entitlement_required'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'bad_request'
  | 'error';

export function classifyMigrationFailure(err: unknown): MigrationFailure {
  if (isPayrollAddonRequiredError(err)) return 'entitlement_required';
  if (isForbiddenTrpcError(err)) return 'forbidden';
  if (isNotFoundTrpcError(err)) return 'not_found';
  if (isConflictTrpcError(err)) return 'conflict';
  if (isBadRequestTrpcError(err)) return 'bad_request';
  return 'error';
}

export type MigrationResult<T> =
  | { outcome: 'success'; value: T }
  | { outcome: 'failure'; failure: MigrationFailure };

// ── Deps (injected for tests) ───────────────────────────────────────────────

export interface MigrationDeps {
  template: (input: MigrationEntityInput) => Promise<MigrationTemplateInfo>;
  stage: (
    input: MigrationEntityInput & MigrationStageInput,
  ) => Promise<MigrationReport>;
  getReport: (
    input: MigrationEntityInput & { run_id: string },
  ) => Promise<MigrationRunReport>;
  listRuns: (input: MigrationEntityInput) => Promise<MigrationRunSummary[]>;
  commit: (
    input: MigrationEntityInput & {
      run_id: string;
      acknowledge_warnings: boolean;
    },
  ) => Promise<MigrationCommitProgress>;
  undo: (
    input: MigrationEntityInput & { run_id: string; confirm: true },
  ) => Promise<MigrationUndoResult>;
  abort: (
    input: MigrationEntityInput & { run_id: string },
  ) => Promise<{ status: string }>;
}

function defaultMigrationDeps(): MigrationDeps {
  const client = getTrpcClient();
  return {
    template: (input) => client.wageproMigration.template.query(input),
    stage: (input) => client.wageproMigration.stage.mutate(input),
    getReport: (input) => client.wageproMigration.getReport.query(input),
    listRuns: (input) => client.wageproMigration.listRuns.query(input),
    commit: (input) => client.wageproMigration.commit.mutate(input),
    undo: (input) => client.wageproMigration.undo.mutate(input),
    abort: (input) => client.wageproMigration.abort.mutate(input),
  };
}

// ── Pure, never-throwing action functions (exported for DI tests) ───────────

export async function fetchMigrationTemplate(
  entity: MigrationEntityInput,
  deps?: MigrationDeps,
): Promise<MigrationResult<MigrationTemplateInfo>> {
  try {
    const value = await (deps ?? defaultMigrationDeps()).template(entity);
    return { outcome: 'success', value };
  } catch (err) {
    console.error('[wagepro-web] wageproMigration: template failed:', err);
    return { outcome: 'failure', failure: classifyMigrationFailure(err) };
  }
}

export async function stageMigrationRun(
  entity: MigrationEntityInput,
  input: MigrationStageInput,
  deps?: MigrationDeps,
): Promise<MigrationResult<MigrationReport>> {
  try {
    const value = await (deps ?? defaultMigrationDeps()).stage({
      ...entity,
      ...input,
    });
    return { outcome: 'success', value };
  } catch (err) {
    console.error('[wagepro-web] wageproMigration: stage failed:', err);
    return { outcome: 'failure', failure: classifyMigrationFailure(err) };
  }
}

export async function fetchMigrationReport(
  entity: MigrationEntityInput,
  runId: string,
  deps?: MigrationDeps,
): Promise<MigrationResult<MigrationRunReport>> {
  try {
    const value = await (deps ?? defaultMigrationDeps()).getReport({
      ...entity,
      run_id: runId,
    });
    return { outcome: 'success', value };
  } catch (err) {
    console.error('[wagepro-web] wageproMigration: getReport failed:', err);
    return { outcome: 'failure', failure: classifyMigrationFailure(err) };
  }
}

export async function listMigrationRuns(
  entity: MigrationEntityInput,
  deps?: MigrationDeps,
): Promise<MigrationResult<MigrationRunSummary[]>> {
  try {
    const value = await (deps ?? defaultMigrationDeps()).listRuns(entity);
    return { outcome: 'success', value };
  } catch (err) {
    console.error('[wagepro-web] wageproMigration: listRuns failed:', err);
    return { outcome: 'failure', failure: classifyMigrationFailure(err) };
  }
}

export async function commitMigrationRun(
  entity: MigrationEntityInput,
  runId: string,
  acknowledgeWarnings: boolean,
  deps?: MigrationDeps,
): Promise<MigrationResult<MigrationCommitProgress>> {
  try {
    const value = await (deps ?? defaultMigrationDeps()).commit({
      ...entity,
      run_id: runId,
      acknowledge_warnings: acknowledgeWarnings,
    });
    return { outcome: 'success', value };
  } catch (err) {
    console.error('[wagepro-web] wageproMigration: commit failed:', err);
    return { outcome: 'failure', failure: classifyMigrationFailure(err) };
  }
}

export async function undoMigrationRun(
  entity: MigrationEntityInput,
  runId: string,
  deps?: MigrationDeps,
): Promise<MigrationResult<MigrationUndoResult>> {
  try {
    const value = await (deps ?? defaultMigrationDeps()).undo({
      ...entity,
      run_id: runId,
      confirm: true,
    });
    return { outcome: 'success', value };
  } catch (err) {
    console.error('[wagepro-web] wageproMigration: undo failed:', err);
    return { outcome: 'failure', failure: classifyMigrationFailure(err) };
  }
}

export async function abortMigrationRun(
  entity: MigrationEntityInput,
  runId: string,
  deps?: MigrationDeps,
): Promise<MigrationResult<{ status: string }>> {
  try {
    const value = await (deps ?? defaultMigrationDeps()).abort({
      ...entity,
      run_id: runId,
    });
    return { outcome: 'success', value };
  } catch (err) {
    console.error('[wagepro-web] wageproMigration: abort failed:', err);
    return { outcome: 'failure', failure: classifyMigrationFailure(err) };
  }
}

// ── Hook ────────────────────────────────────────────────────────────────────

export interface UseWageproMigrationResult {
  entity_status: 'ready' | 'no_entity';
  /** False for a read-only OAuth grant; write actions are then undefined. */
  can_write: boolean;
  template: (() => Promise<MigrationResult<MigrationTemplateInfo>>) | undefined;
  getReport:
    | ((runId: string) => Promise<MigrationResult<MigrationRunReport>>)
    | undefined;
  listRuns: (() => Promise<MigrationResult<MigrationRunSummary[]>>) | undefined;
  stage:
    | ((
        input: MigrationStageInput,
      ) => Promise<MigrationResult<MigrationReport>>)
    | undefined;
  commit:
    | ((
        runId: string,
        acknowledgeWarnings: boolean,
      ) => Promise<MigrationResult<MigrationCommitProgress>>)
    | undefined;
  undo:
    | ((runId: string) => Promise<MigrationResult<MigrationUndoResult>>)
    | undefined;
  abort:
    | ((runId: string) => Promise<MigrationResult<{ status: string }>>)
    | undefined;
}

export function useWageproMigration(): UseWageproMigrationResult {
  const entityContext = useOAuthEntityContext();
  const { grant } = useAuth();
  const readyEntity = entityContext.status === 'ready' ? entityContext : null;
  const canWrite = readyEntity !== null && isWriteCapableGrant(grant);

  const entity = React.useMemo<MigrationEntityInput | null>(
    () =>
      readyEntity
        ? { firm_id: readyEntity.firm_id, entity_id: readyEntity.entity_id }
        : null,
    [readyEntity],
  );

  const template = React.useMemo(
    () => (entity ? () => fetchMigrationTemplate(entity) : undefined),
    [entity],
  );
  const getReport = React.useMemo(
    () =>
      entity
        ? (runId: string) => fetchMigrationReport(entity, runId)
        : undefined,
    [entity],
  );
  const listRuns = React.useMemo(
    () => (entity ? () => listMigrationRuns(entity) : undefined),
    [entity],
  );
  const stage = React.useMemo(
    () =>
      entity && canWrite
        ? (input: MigrationStageInput) => stageMigrationRun(entity, input)
        : undefined,
    [entity, canWrite],
  );
  const commit = React.useMemo(
    () =>
      entity && canWrite
        ? (runId: string, ack: boolean) =>
            commitMigrationRun(entity, runId, ack)
        : undefined,
    [entity, canWrite],
  );
  const undo = React.useMemo(
    () =>
      entity && canWrite
        ? (runId: string) => undoMigrationRun(entity, runId)
        : undefined,
    [entity, canWrite],
  );
  const abort = React.useMemo(
    () =>
      entity && canWrite
        ? (runId: string) => abortMigrationRun(entity, runId)
        : undefined,
    [entity, canWrite],
  );

  return {
    entity_status: readyEntity ? 'ready' : 'no_entity',
    can_write: canWrite,
    template,
    getReport,
    listRuns,
    stage,
    commit,
    undo,
    abort,
  };
}
